import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}
interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Top-level error boundary. Catches any unhandled render errors in the tree
 * and shows a recovery UI instead of a white screen.
 *
 * Placed at the root of the app so a crash in one page component never
 * takes down the entire dashboard.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[MCT] Unhandled render error:", error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-screen bg-bg">
          <div className="text-center max-w-md px-6">
            <div className="text-red-400 text-5xl font-bold mb-4">!</div>
            <h1 className="text-lg font-semibold text-red-400 mb-2">Something went wrong</h1>
            <p className="text-sm text-gray-400 mb-1">
              An unexpected error occurred in the dashboard.
            </p>
            <p className="text-xs text-gray-500 font-mono bg-gray-900 rounded px-3 py-2 mb-5 break-all">
              {this.state.error?.message ?? "Unknown error"}
            </p>
            <button
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded transition-colors"
              onClick={() => this.setState({ hasError: false, error: null })}
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
